import React, { createContext, useContext, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { translations, DA_TO_EN_SLUGS, EN_TO_DA_SLUGS } from '../translations'

const LanguageContext = createContext()

// Map of static routes between languages
const ROUTE_MAP = {
  da: {
    '/': '/en',
    '/kontakt': '/en/contact'
  },
  en: {
    '/en': '/',
    '/en/contact': '/kontakt'
  }
}

export function LanguageProvider({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams()

  // Detect language from URL path
  const isEnglish = location.pathname.startsWith('/en')
  const language = isEnglish ? 'en' : 'da'

  // Translation helper
  const t = (key) => {
    const langDict = translations[language] || translations.da
    return langDict[key] || translations.da[key] || key
  }

  // Generate language-aware links
  const linkTo = (path) => {
    if (language === 'en') {
      if (path === '/') return '/en'
      if (path === '/kontakt') return '/en/contact'
      if (path.startsWith('#')) return `/en${path}`
      if (path.startsWith('/ydelser/')) {
        const slug = path.replace('/ydelser/', '')
        const enSlug = DA_TO_EN_SLUGS[slug] || slug
        return `/en/services/${enSlug}`
      }
      return `/en${path}`
    } else {
      if (path === '/en') return '/'
      if (path === '/en/contact') return '/kontakt'
      if (path.startsWith('#')) return `/${path}`
      if (path.startsWith('/en/services/')) {
        const slug = path.replace('/en/services/', '')
        const daSlug = EN_TO_DA_SLUGS[slug] || slug
        return `/ydelser/${daSlug}`
      }
      return path
    }
  }

  // Change language and redirect to corresponding page
  const changeLanguage = (newLang) => {
    if (newLang === language) return

    const currentPath = location.pathname

    // 1. Check static routes
    if (ROUTE_MAP[language] && ROUTE_MAP[language][currentPath]) {
      navigate(ROUTE_MAP[language][currentPath] + location.hash)
      return
    }

    // 2. Check home with hash or trailing slash
    if (currentPath === '/' && newLang === 'en') {
      navigate('/en' + location.hash)
      return
    }
    if (currentPath === '/en' && newLang === 'da') {
      navigate('/' + location.hash)
      return
    }

    // 3. Handle service detail pages with slug mapping
    if (language === 'da' && currentPath.startsWith('/ydelser/')) {
      const daSlug = currentPath.split('/').pop()
      const enSlug = DA_TO_EN_SLUGS[daSlug] || daSlug
      navigate(`/en/services/${enSlug}`)
      return
    }

    if (language === 'en' && currentPath.startsWith('/en/services/')) {
      const enSlug = currentPath.split('/').pop()
      const daSlug = EN_TO_DA_SLUGS[enSlug] || enSlug
      navigate(`/ydelser/${daSlug}`)
      return
    }

    // Default fallback
    if (newLang === 'en') {
      navigate('/en')
    } else {
      navigate('/')
    }
  }

  // Update document lang attribute and meta tags for SEO
  useEffect(() => {
    document.documentElement.lang = language

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', window.location.origin + location.pathname)

    // Update hreflang tags
    let hreflangDa = document.querySelector('link[hreflang="da"]')
    if (!hreflangDa) {
      hreflangDa = document.createElement('link')
      hreflangDa.setAttribute('rel', 'alternate')
      hreflangDa.setAttribute('hreflang', 'da')
      document.head.appendChild(hreflangDa)
    }
    
    let hreflangEn = document.querySelector('link[hreflang="en"]')
    if (!hreflangEn) {
      hreflangEn = document.createElement('link')
      hreflangEn.setAttribute('rel', 'alternate')
      hreflangEn.setAttribute('hreflang', 'en')
      document.head.appendChild(hreflangEn)
    }

    let hreflangDefault = document.querySelector('link[hreflang="x-default"]')
    if (!hreflangDefault) {
      hreflangDefault = document.createElement('link')
      hreflangDefault.setAttribute('rel', 'alternate')
      hreflangDefault.setAttribute('hreflang', 'x-default')
      document.head.appendChild(hreflangDefault)
    }

    // Determine alternate URLs
    const currentPath = location.pathname
    let altPathDa = '/'
    let altPathEn = '/en'

    if (currentPath === '/' || currentPath === '/en') {
      altPathDa = '/'
      altPathEn = '/en'
    } else if (currentPath === '/kontakt' || currentPath === '/en/contact') {
      altPathDa = '/kontakt'
      altPathEn = '/en/contact'
    } else if (currentPath.startsWith('/ydelser/') || currentPath.startsWith('/en/services/')) {
      // For service pages, we can't easily map the slug here without context,
      // but we can set it to the base or let the page component handle it.
      altPathDa = currentPath.replace('/en/services/', '/ydelser/')
      altPathEn = currentPath.replace('/ydelser/', '/en/services/')
    }

    hreflangDa.setAttribute('href', window.location.origin + altPathDa)
    hreflangEn.setAttribute('href', window.location.origin + altPathEn)
    hreflangDefault.setAttribute('href', window.location.origin + altPathDa) // Danish is default

  }, [language, location.pathname])

  return (
    <LanguageContext.Provider value={{ language, t, linkTo, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
